import { ApiResponse, PaginatedApi } from '@models/common/api';
import {
  IAiReviewActionRequest,
  IAnalyticsRequest,
  IAnalyticsTopicsRequest,
  ICreateReviewRespRequest,
  IGrowthOpportunitiesRequest,
  IFindReviewsRequest,
  IPutAiResponderResponseRequest,
  IAiRsponderSettings,
  IPutReviewSourcesRequest,
  ICreateReviewCampaignRequest,
  IUpdateReviewCampaignRequest,
  IFindManyReviewCampaignRequest,
  ICreateInternalReviewRequest,
  IUpdateReviewRespRequest,
  IDeleteReviewRespRequest,
  IRefetchReviewRespRequest,
  IMarkReviewsAsViewedRequest,
  ISentimentDistributionRequest,
  ISentimentOverTimeRequest,
  IUpdateReviewCampaignStatusRequest,
} from '@models/reviews/requests';
import {
  IAnalyticsOverviewResponse,
  IAnalyticsPublishersResponse,
  IAnalyticsRatingResponse,
  IAnalyticsTimelineResponse,
  IAnalyticsTopicsResponse,
  IGrowthOpportunitiesResponse,
  IFindReviewsList,
  IMentionsResponse,
  IGetReviewsSourcesResponse,
  ICreateReviewRespResponse,
  IPutReviewsSourcesResponse,
  IAiReviewResponseList,
  IAiReviewResponseAction,
  IAiReviewResponse,
  IFindOneReviewCampaignResponse,
  IFindOneCampaignDataResponse,
  ICreateInternalReviewResponse,
  IReviewCampaignAnalyticsResponse,
  ISentimentDistributionResponse,
  ISentimentOverTimeResponse,
} from '@models/reviews/response';

import { del, get, patch, post, put } from './methods';

const BASE_URL = 'reviews';

const ReviewsAPI = {
  find: async (params: IFindReviewsRequest) => {
    const response = await get<PaginatedApi<IFindReviewsList>>(
      BASE_URL,
      {
        params,
        paramsSerializer: { indexes: true },
      },
      2,
    );
    return response.data;
  },
  refetchOne: async (body: IRefetchReviewRespRequest) => {
    const response = await get<ApiResponse<ICreateReviewRespResponse>>(
      `${BASE_URL}/${body.reviewId}`,
      { params: { locationId: body.locationId } },
      2,
    );
    return response.data;
  },
  create: async (body: ICreateReviewRespRequest) => {
    const response = await post<ApiResponse<ICreateReviewRespResponse>>(`${BASE_URL}/reply`, body, undefined, 2);
    return response.data;
  },
  update: async (body: IUpdateReviewRespRequest) => {
    const response = await put<ApiResponse<ICreateReviewRespResponse>>(`${BASE_URL}/reply`, body, undefined, 2);
    return response.data;
  },
  delete: async (body: IDeleteReviewRespRequest) => {
    const response = await del<ApiResponse<ICreateReviewRespResponse>>(`${BASE_URL}/reply`, { data: body }, 2);
    return response.data;
  },
  markAsViewed: async (body: IMarkReviewsAsViewedRequest) => {
    const response = await patch<ApiResponse<void>>(`${BASE_URL}`, body, undefined, 2);
    return response.data;
  },
  analyticsOverview: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IAnalyticsOverviewResponse>>(
      `${BASE_URL}/analytics/overview`,
      { params },
      2,
    );
    return response.data;
  },
  analyticsRating: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IAnalyticsRatingResponse>>(`${BASE_URL}/analytics/ratings`, { params });
    return response.data;
  },
  analyticsTimeline: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IAnalyticsTimelineResponse>>(`${BASE_URL}/analytics/timeline`, { params });
    return response.data;
  },
  analyticsPublishers: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IAnalyticsPublishersResponse>>(`${BASE_URL}/analytics/publishers`, {
      params,
    });
    return response.data;
  },
  analyticsPhrases: async (params: IAnalyticsRequest) => {
    const response = await get<ApiResponse<IMentionsResponse[]>>(`${BASE_URL}/analytics/phrases`, { params });
    return response.data;
  },
  analyticsTopics: async (params: IAnalyticsTopicsRequest) => {
    const response = await get<ApiResponse<IAnalyticsTopicsResponse>>(`${BASE_URL}/analytics/topics`, { params }, 2);
    return response.data;
  },
  growthOpportunities: async (params: IGrowthOpportunitiesRequest) => {
    const response = await get<ApiResponse<IGrowthOpportunitiesResponse>>(
      `${BASE_URL}/analytics/growth-opportunities`,
      { params },
      2,
    );
    return response.data;
  },
  getReviewsSources: async (locationId: number) => {
    const response = await get<ApiResponse<IGetReviewsSourcesResponse>>(`${BASE_URL}/sources`, {
      params: { locationId },
    });
    return response.data;
  },
  putReviewsSources: async (body: IPutReviewSourcesRequest) => {
    const response = await put<ApiResponse<IPutReviewsSourcesResponse>>(`${BASE_URL}/sources`, body);
    return response.data;
  },
  findAiResponderSettings: async (organizationId: number) => {
    const response = await get<ApiResponse<IAiRsponderSettings>>(`${BASE_URL}/ai-responder-settings`, {
      params: { organizationId },
    });
    return response.data;
  },
  putAiResponderSettings: async (organizationId: number, body: IAiRsponderSettings) => {
    const response = await put<ApiResponse<IAiRsponderSettings>>(
      `${BASE_URL}/ai-responder-settings`,
      body,
      { params: { organizationId } },
      1,
    );
    return response.data;
  },
  getAiResponderResponses: async (locationId: number) => {
    const response = await get<ApiResponse<IAiReviewResponseList>>(
      `${BASE_URL}/ai-generated-responses`,
      {
        params: { locationId, includeReviews: true, fetchAll: true },
      },
      2,
    );
    return response.data;
  },
  updateAiResponderResponse: async ({ locationId, id, content }: IPutAiResponderResponseRequest) => {
    const response = await patch<ApiResponse<IAiReviewResponse>>(
      `${BASE_URL}/ai-generated-responses`,
      { content, locationId, aiGeneratedResponseId: id },
      undefined,
      2,
    );
    return response.data;
  },
  actionAiResponderResponses: async ({ locationId, ids, action }: IAiReviewActionRequest) => {
    const response = await put<ApiResponse<IAiReviewResponseAction>>(
      `${BASE_URL}/ai-generated-responses`,
      {
        locationId,
        ids,
        action,
      },
      undefined,
      2,
    );
    return response.data;
  },
  reviewCampaign: {
    create: async (body: ICreateReviewCampaignRequest) => {
      const response = await post<ApiResponse<IFindOneReviewCampaignResponse>>(`${BASE_URL}/campaigns`, body);
      return response.data;
    },
    findMany: async ({ locationId, pageSize, currentPage, status, content }: IFindManyReviewCampaignRequest) => {
      const response = await get<PaginatedApi<IFindOneReviewCampaignResponse[]>>(`${BASE_URL}/campaigns`, {
        params: { locationId, pageSize, currentPage, status, content },
      });
      return response.data;
    },
    findOne: async (campaignId: string) => {
      const response = await get<ApiResponse<IFindOneCampaignDataResponse>>(
        `${BASE_URL}/campaigns/replies/${campaignId}`,
      );
      return response.data;
    },
    update: async (id: number, body: IUpdateReviewCampaignRequest) => {
      const response = await put<ApiResponse<IFindOneReviewCampaignResponse>>(`${BASE_URL}/campaigns/${id}`, body);
      return response.data;
    },
    updateStatus: async (id: number, body: IUpdateReviewCampaignStatusRequest) => {
      const response = await patch<ApiResponse<IFindOneReviewCampaignResponse>>(`${BASE_URL}/campaigns/${id}`, body);
      return response.data;
    },
    submitInternalReview: async (campaignSlug: string, body: ICreateInternalReviewRequest) => {
      const response = await post<ApiResponse<ICreateInternalReviewResponse>>(
        `${BASE_URL}/campaigns/replies/${campaignSlug}`,
        body,
      );
      return response.data;
    },
    analytics: async (campaignSlug: string) => {
      const response = await get<ApiResponse<IReviewCampaignAnalyticsResponse>>(
        `${BASE_URL}/campaigns/analytics/${campaignSlug}`,
      );
      return response.data;
    },
  },
  sentiment: {
    distribution: async (query: ISentimentDistributionRequest) => {
      const response = await get<ApiResponse<ISentimentDistributionResponse[]>>(
        `${BASE_URL}/analytics/distribution`,
        {
          params: { ...query },
        },
        2,
      );
      return response.data;
    },

    overtime: async (query: ISentimentOverTimeRequest) => {
      const response = await get<ApiResponse<ISentimentOverTimeResponse>>(
        `${BASE_URL}/analytics/reviews-over-time`,
        {
          params: { ...query },
        },
        2,
      );
      return response.data;
    },
  },
};

export default ReviewsAPI;
